import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import {
  PatientData,
  PatientStatus,
  ProviderAssignment,
  PatientHoldRequest,
  PatientDischargeRequest,
  PatientHoldHistoryEntry,
} from '../../dtos/admin/patient.dto';
import { PatientFormData } from '../../types/admin/patient'; // UI-specific type stays in types
import { TokenService } from '../auth/token.service';

export type { PatientHoldHistoryEntry } from '../../dtos/admin/patient.dto';

const KNOWN_PATIENT_STATUSES: readonly PatientStatus[] = ['PENDING_ADMISSION', 'ACTIVE', 'ON_HOLD', 'INACTIVE'];

/**
 * Normalize a backend patient status (lowercase `pending_admission | active | on_hold | inactive`,
 * or legacy uppercase / spaced variants) to the uppercase PatientStatus used in the UI.
 * Unknown values map to INACTIVE so they never look like an active census patient.
 */
export const normalizePatientStatus = (status: unknown): PatientStatus => {
  const normalized = String(status || 'ACTIVE').trim().toUpperCase().replace(/[\s-]+/g, '_');
  return (KNOWN_PATIENT_STATUSES as readonly string[]).includes(normalized)
    ? (normalized as PatientStatus)
    : 'INACTIVE';
};

type RawPatient = Record<string, unknown>;

const str = (value: unknown): string => (typeof value === 'string' ? value : value == null ? '' : String(value));
const optStr = (value: unknown): string | null | undefined =>
  value === undefined ? undefined : value === null ? null : String(value);

/** Map one backend patient row to PatientData (shared by list, get, create, update). */
export const mapPatientResponse = (patient: RawPatient): PatientData => ({
  id: str(patient.patientId ?? patient.id),
  tenantId: str(patient.tenantId),
  cognitoUserId: optStr(patient.cognitoUserId),
  mrn: str(patient.mrn),
  firstName: str(patient.firstName),
  lastName: str(patient.lastName),
  dob: str(patient.dob),
  email: optStr(patient.email) ?? undefined,
  gender: str(patient.gender),
  phoneNumber: str(patient.phoneNumber),
  status: normalizePatientStatus(patient.status),
  profilePictureUrl: optStr(patient.profilePictureUrl) || null,
  familyPortalEnabled: patient.familyPortalEnabled === true,
  holdReasonCode: optStr(patient.holdReasonCode),
  holdNote: optStr(patient.holdNote),
  holdStartDate: optStr(patient.holdStartDate),
  holdExpectedReturnDate: optStr(patient.holdExpectedReturnDate),
  holdSetBy: optStr(patient.holdSetBy),
  holdSetAt: optStr(patient.holdSetAt),
  socDate: optStr(patient.socDate),
  dischargeReason: optStr(patient.dischargeReason),
  dischargeDate: optStr(patient.dischargeDate),
  deceasedDate: optStr(patient.deceasedDate),
  isSample: patient.isSample === true || patient.is_sample === true,
  insuranceSummary: patient.insuranceSummary && typeof patient.insuranceSummary === 'object'
    ? (patient.insuranceSummary as PatientData['insuranceSummary'])
    : null,
  createdAt: optStr(patient.createdAt) ?? undefined,
  updatedAt: optStr(patient.updatedAt) ?? undefined,
});

/**
 * Response interface for fetching patients list
 */
interface FetchPatientsResponse {
  patients: any[];
  totalCount?: number;
  total?: number;
  items?: any[];
  results?: any[];
  pagination?: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Response interface for fetching patient provider assignments
 */
interface FetchPatientProviderAssignmentsResponse {
  assignments?: any[];
}

/**
 * Response interface for fetching my patients
 */
interface FetchMyPatientsResponse {
  patients: any[];
  items?: any[];
}

/**
 * Fetch all patients with optional filtering
 */
export const fetchPatients = async (
  page: number = 1,
  limit: number = 100,
  search?: string,
  status?: string
): Promise<{ patients: PatientData[], totalCount: number, pagination: any, hasMore: boolean }> => {
  try {
    // Build query parameters
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };

    if (search) {
      params.search = search;
    }

    if (status && status !== 'All') {
      params.status = status;
    }

    const response: ApiResponse<FetchPatientsResponse> = await apiClient.get('/patients', params);

    if (response.success && response.data) {
      const data = response.data;

      // Handle different API response formats and provide defaults
      const apiPatients = data.patients || data.items || data.results || [];
        // Map response data to match our frontend model
      const patients: PatientData[] = Array.isArray(apiPatients) ? apiPatients.map((patient: RawPatient) => mapPatientResponse(patient)) : [];

      // Extract pagination data if available
      const pagination = data.pagination || response.pagination || {
        page,
        limit,
        totalCount: data.totalCount || data.total || patients.length,
        totalPages: Math.ceil((data.totalCount || data.total || patients.length) / limit),
        hasNext: ((page * limit) < (data.totalCount || data.total || patients.length)),
        hasPrev: page > 1
      };

      const totalCount = data.totalCount || data.total || pagination.totalCount || patients.length;
      const pageInfo = pagination as { hasNext?: boolean; hasNextPage?: boolean };
      const hasMore = pageInfo.hasNext ?? pageInfo.hasNextPage ?? page * limit < totalCount;

      return {
        patients,
        totalCount,
        pagination,
        hasMore
      };
    }

    throw new Error(response.error?.message || 'Failed to fetch patients');
  } catch (error) {
    console.error('Error fetching patients:', error);
    throw error;
  }
};

/**
 * Fetch a single patient by ID
 */
export const fetchPatientById = async (patientId: string): Promise<PatientData> => {
  try {
    const response: ApiResponse<any> = await apiClient.get(`/patients/${patientId}`);

    if (response.success && response.data) {
      return mapPatientResponse(response.data as RawPatient);
    }

    throw new Error(response.error?.message || `Failed to fetch patient ${patientId}`);
  } catch (error) {
    console.error(`Error fetching patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Place a patient on hold (HLD-03). The backend reads `holdReasonCode` (not `reasonCode`);
 * a note is required when the reason is OTHER.
 */
export const putPatientOnHold = async (
  patientId: string,
  payload: PatientHoldRequest
): Promise<PatientData> => {
  const body: Record<string, string> = { status: 'on_hold', holdReasonCode: payload.holdReasonCode };
  if (payload.holdNote?.trim()) body.holdNote = payload.holdNote.trim();
  if (payload.holdStartDate) body.holdStartDate = payload.holdStartDate;
  if (payload.holdExpectedReturnDate) body.holdExpectedReturnDate = payload.holdExpectedReturnDate;
  const response: ApiResponse<RawPatient> = await apiClient.put(`/patients/${patientId}`, body);
  if (response.success) {
    return fetchPatientById(patientId);
  }
  throw new Error(response.error?.message || 'Failed to put the patient on hold');
};

/** Resume a held patient (on_hold -> active). */
export const resumePatient = async (patientId: string): Promise<PatientData> => {
  const response: ApiResponse<RawPatient> = await apiClient.put(`/patients/${patientId}`, { status: 'active' });
  if (response.success) {
    return fetchPatientById(patientId);
  }
  throw new Error(response.error?.message || 'Failed to resume the patient');
};

/**
 * Discharge a patient (DSC-02): active/on_hold -> inactive with a reason and date.
 * Requires admin:patients:discharge (403 DISCHARGE_NOT_PERMITTED otherwise).
 */
export const dischargePatient = async (
  patientId: string,
  payload: PatientDischargeRequest
): Promise<PatientData> => {
  const body: Record<string, unknown> = {
    status: 'inactive',
    dischargeReason: payload.dischargeReason,
    dischargeDate: payload.dischargeDate,
  };
  if (payload.dischargeReason === 'DECEASED' && payload.deceasedDate) {
    body.deceasedDate = payload.deceasedDate;
  }
  // Phase 16E (DSC-19): the notice record; not used for a Deceased discharge
  if (payload.dischargeReason !== 'DECEASED' && payload.dischargeNotice) {
    body.dischargeNotice = payload.dischargeNotice;
  }
  const response: ApiResponse<RawPatient> = await apiClient.put(`/patients/${patientId}`, body);
  if (response.success) {
    return fetchPatientById(patientId);
  }
  throw new Error(response.error?.message || 'Failed to discharge the patient');
};

/**
 * Readmit a discharged patient (DSC-07): inactive -> pending_admission with an audited reason.
 * The patient then goes through Admit again. Requires admin:patients:discharge.
 */
export const readmitPatient = async (patientId: string, correctionReason: string): Promise<PatientData> => {
  const response: ApiResponse<RawPatient> = await apiClient.put(`/patients/${patientId}`, {
    status: 'pending_admission',
    correctionReason,
  });
  if (response.success) {
    return fetchPatientById(patientId);
  }
  throw new Error(response.error?.message || 'Failed to readmit the patient');
};

export const fetchPatientHoldHistory = async (patientId: string): Promise<PatientHoldHistoryEntry[]> => {
  const response: ApiResponse<PatientHoldHistoryEntry[] | { items?: PatientHoldHistoryEntry[]; history?: PatientHoldHistoryEntry[] }> =
    await apiClient.get(`/patients/${patientId}/hold-history`);
  if (response.success && response.data) {
    return Array.isArray(response.data) ? response.data : response.data.items || response.data.history || [];
  }
  throw new Error(response.error?.message || `Failed to load hold history for patient ${patientId}`);
};

/**
 * Create a new patient
 */
export const createPatient = async (patientData: PatientFormData): Promise<PatientData> => {
  try {
    // Normalize status to uppercase and validate for payload
    const validStatus = patientData.status ? normalizePatientStatus(patientData.status) : 'ACTIVE';

    // Map frontend model to API model
    const payload: any = {
      firstName: patientData.firstName,
      lastName: patientData.lastName,
      dob: patientData.dob,
      email: patientData.email,
      gender: patientData.gender,
      phoneNumber: patientData.phoneNumber,
      status: validStatus.toLowerCase()
    };

    // Include family portal data if enabled
    if (patientData.familyPortalEnabled || patientData.familyPortal) {
      // Build familyPortal object - prefer explicit familyPortal structure over legacy flag
      payload.familyPortal = patientData.familyPortal || {
        enabled: patientData.familyPortalEnabled || false,
        guardian: undefined
      };
    }

    const response: ApiResponse<any> = await apiClient.post('/patients', payload);

    if (response.success && response.data) {
      return mapPatientResponse(response.data as RawPatient);
    }

    throw new Error(response.error?.message || 'Failed to create patient');
  } catch (error) {
    console.error('Error creating patient:', error);
    throw error;
  }
};

/**
 * Update an existing patient
 * Note: MRN cannot be updated after creation as it's immutable
 */
export const updatePatient = async (patientId: string, patientData: PatientFormData): Promise<PatientData> => {
  try {
    // Normalize status to uppercase and validate
    const validStatus = patientData.status ? normalizePatientStatus(patientData.status) : 'ACTIVE';

    // Map frontend model to API model
    const payload: any = {
      firstName: patientData.firstName,
      lastName: patientData.lastName,
      dob: patientData.dob,
      email: patientData.email,
      gender: patientData.gender,
      phoneNumber: patientData.phoneNumber,
      status: validStatus.toLowerCase()
    };

    // Include family portal data if provided
    if (patientData.familyPortalEnabled !== undefined || patientData.familyPortal) {
      // Build familyPortal object - prefer explicit familyPortal structure over legacy flag
      payload.familyPortal = patientData.familyPortal || {
        enabled: patientData.familyPortalEnabled || false,
        guardian: undefined
      };
    }

    const response: ApiResponse<any> = await apiClient.put(`/patients/${patientId}`, payload);

    if (response.success && response.data) {
      return mapPatientResponse(response.data as RawPatient);
    }

    throw new Error(response.error?.message || `Failed to update patient ${patientId}`);
  } catch (error) {
    console.error(`Error updating patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Get all provider assignments for a patient
 * The updated API now includes provider details (firstName, lastName, etc.)
 */
export const fetchPatientProviderAssignments = async (patientId: string): Promise<ProviderAssignment[]> => {
  try {
    const response: ApiResponse<FetchPatientProviderAssignmentsResponse | any[]> = await apiClient.get(`/patientmr/${patientId}/providers`);

    if (response.success && response.data) {
      // Handle different API response structures
      const assignments = Array.isArray(response.data) ? response.data : (response.data.assignments || []);

      return Array.isArray(assignments) ? assignments.map((assignment: any) => {
        // Extract provider details from the assignment response
        // The API now includes these fields directly in the response

        return {
          assignmentId: assignment.assignmentId,
          tenantId: assignment.tenantId,
          patientId: assignment.patientId,
          userId: assignment.userId,
          assignmentType: assignment.assignmentType,
          startDate: assignment.startDate,
          endDate: assignment.endDate || null,
          status: assignment.status === 'active' ? 'active' : 'inactive',
          createdAt: assignment.createdAt,
          updatedAt: assignment.updatedAt,
          // Include provider details in the assignment
          provider: {
            firstName: assignment.providerFirstName || '',
            lastName: assignment.providerLastName || '',
            middleName: assignment.middleName || '',
            discipline: assignment.providerType || '',
            userId: assignment.userId
          }
        };
      }) : [];
    }

    throw new Error(response.error?.message || `Failed to fetch provider assignments for patient ${patientId}`);
  } catch (error) {
    console.error(`Error fetching provider assignments for patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Assign a provider to a patient
 */
export const assignProviderToPatient = async (
  patientId: string,
  userId: string,
  assignmentType: string,
  startDate: string,
  endDate?: string,
  status: string = 'active'
): Promise<ProviderAssignment> => {
  try {
    const payload = {
      userId,
      assignmentType,
      startDate,
      endDate,
      status
    };

    const response: ApiResponse<any> = await apiClient.post(`/patientmr/${patientId}/providers`, payload);

    if (response.success && response.data) {
      const assignment = response.data;

      return {
        assignmentId: assignment.assignmentId,
        tenantId: assignment.tenantId,
        patientId: assignment.patientId,
        userId: assignment.userId,
        assignmentType: assignment.assignmentType,
        startDate: assignment.startDate,
        endDate: assignment.endDate || null,
        status: assignment.status === 'active' ? 'active' : 'inactive',
        createdAt: assignment.createdAt,
        updatedAt: assignment.updatedAt
      };
    }

    throw new Error(response.error?.message || `Failed to assign provider to patient ${patientId}`);
  } catch (error) {
    console.error(`Error assigning provider to patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Fetch patients assigned to the current provider (My Patients)
 */
// Cache for preventing duplicate concurrent requests
let myPatientsCache: { promise: Promise<PatientData[]> | null, timestamp: number } = { promise: null, timestamp: 0 };
const CACHE_DURATION = 5000; // 5 seconds

export const fetchMyPatients = async (): Promise<PatientData[]> => {
  // If there's an ongoing request, return that promise
  if (myPatientsCache.promise) {
    console.log('[fetchMyPatients] Returning cached promise (request already in flight)');
    return myPatientsCache.promise;
  }

  // If we have a recent result (within cache duration), reuse it
  const now = Date.now();
  if (myPatientsCache.timestamp && (now - myPatientsCache.timestamp) < CACHE_DURATION) {
    console.log('[fetchMyPatients] Using recent cached result');
    // Return the resolved value from cache
    return myPatientsCache.promise!;
  }

  console.log('[fetchMyPatients] Starting new request');

  // Create new request promise
  const requestPromise = (async () => {
    try {
      // Extract user id (providerId) from the JWT token
      const idToken = TokenService.getIdToken();
      if (!idToken) {
        console.error("No idToken found");
        return [];
      }
      // Import this at the top: import { getUserAttributesFromToken } from '../auth/cognito.service';
      // (If not already imported)
      // @ts-ignore
      const { getUserAttributesFromToken } = await import('../auth/cognito.service');
      const userAttributes = getUserAttributesFromToken(idToken);
      const providerId = userAttributes?.sub;
      if (!providerId) {
        console.error("No providerId (user sub) found in idToken");
        return [];
      }

      const params = {
        providerId: providerId,
        status: 'active',
        idType: 'identityProviderId'
      };

      const response: ApiResponse<FetchMyPatientsResponse> = await apiClient.get(`/patientmr/providers/${providerId}/patients`, params);

      if (response.success && response.data) {
        const apiPatients = response.data.patients || response.data.items || [];

        if (!Array.isArray(apiPatients)) {
          console.error("API did not return an array of patients:", apiPatients);
          return [];
        }
        // Map response data to match our frontend model
        const patients: PatientData[] = apiPatients.map((patient: any) => {
          // Normalize status to uppercase and validate
          const normalizedStatus = normalizePatientStatus(patient.status);
          return {
            id: patient.patientId || patient.id, // Handle different API response formats
            tenantId: patient.tenantId,
            cognitoUserId: patient.cognitoUserId,
            mrn: patient.mrn || 'Unknown',
            firstName: patient.firstName || 'Unknown',
            lastName: patient.lastName || 'User',
            dob: patient.dob || '2000-01-01',
            email: patient.email,
            gender: patient.gender || 'Unknown',
            phoneNumber: patient.phoneNumber,
            status: normalizedStatus,
            profilePictureUrl: patient.profilePictureUrl || null,
            createdAt: patient.createdAt,
            updatedAt: patient.updatedAt
          };
        });

        // Update cache timestamp after successful fetch
        myPatientsCache.timestamp = Date.now();
        console.log('[fetchMyPatients] Request completed successfully');
        return patients;
      }

      throw new Error(response.error?.message || 'Failed to fetch my patients');
    } catch (error) {
      console.error('Error fetching my patients:', error);
      // Clear cache on error
      myPatientsCache.promise = null;
      myPatientsCache.timestamp = 0;
      throw error; // Make sure to re-throw the error for proper handling
    } finally {
      // Clear the promise after a short delay to allow concurrent calls to use it
      setTimeout(() => {
        if (myPatientsCache.promise === requestPromise) {
          myPatientsCache.promise = null;
        }
      }, 100);
    }
  })();

  // Store the promise
  myPatientsCache.promise = requestPromise;
  return requestPromise;
};

/**
 * Unassign a provider from a patient (remove the provider assignment)
 */
export const unassignProviderFromPatient = async (
  patientId: string,
  providerId: string
): Promise<boolean> => {
  try {
    // Create payload with provider IDs array
    const payload = {
      providerIds: [providerId]
    };

    const response: ApiResponse<any> = await apiClient.post(`/patientmr/${patientId}/providers:unassign`, payload);

    if (response.success) {
      // Return true to indicate successful operation
      return true;
    }

    throw new Error(response.error?.message || `Failed to unassign provider from patient ${patientId}`);
  } catch (error) {
    console.error(`Error unassigning provider from patient ${patientId}:`, error);
    throw error;
  }
};
